import React, { useState, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "../components/L";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import useSEO from "../hooks/useSEO";
import { resolveAssetUrl } from "../utils/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerDetailPage = () => {
  const { tx } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState(null);
  const [events, setEvents] = useState([]);

  useSEO({
    title: trainer ? `${trainer.name} - ${trainer.specialty}` : tx("Profili i Trajnerit"),
    description: trainer ? `${trainer.name}, ${trainer.specialty}. ${trainer.bio?.substring(0, 150)}` : tx("Profili i trajnerit te Albanian Aligners Academy."),
    ogImage: trainer?.image_url,
    canonical: trainer?.slug ? `https://aligners.academy/trajnere/${trainer.slug}` : `https://aligners.academy/trajnere/${id}`,
  });

  useEffect(() => {
    axios.get(`${API}/trainers/${id}`).then(r => {
      // If visitor came via UUID but the trainer has a slug, replace URL with the SEO slug
      if (r.data?.slug && id !== r.data.slug) {
        navigate(`/trajnere/${r.data.slug}`, { replace: true });
      }
      setTrainer(r.data);
    }).catch(() => {});
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
  }, [id, navigate]);

  if (!trainer) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#828D9E]">{tx("Duke ngarkuar...")}</p></div>;

  const imgSrc = resolveAssetUrl(trainer.image_url, "https://images.pexels.com/photos/23625642/pexels-photo-23625642.jpeg");

  const trainerEvents = events.filter(e => e.speaker.toLowerCase().includes(trainer.name.split(" ").pop().toLowerCase()));
  const trainingsList = trainer.trainings ? trainer.trainings.split(",").map(t => t.trim()).filter(Boolean) : [];

  return (
    <div data-testid="trainer-detail-page">
      <section className="relative py-16 md:py-20 px-6" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }}>
        <div className="max-w-[1350px] mx-auto">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
            <Link to="/" className="hover:text-white">{tx("Kryefaqe")}</Link><span>/</span>
            <Link to="/trajnere" className="hover:text-white">{tx("Trajnere")}</Link><span>/</span>
            <span className="text-white">{trainer.name}</span>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 max-w-[1350px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Sidebar - photo */}
          <div className="text-center">
            <div className="w-56 h-56 mx-auto rounded-full overflow-hidden border-4 border-[#1CA3B0]/30 mb-6">
              <img src={imgSrc} alt={trainer.name} className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }} data-testid="trainer-detail-name">{trainer.name}</h1>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#08707A] mt-1">{trainer.specialty}</p>
          </div>

          {/* Content */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Bio")}</h2>
              <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
              <p className="text-[#4A5568] leading-relaxed whitespace-pre-line">{trainer.bio}</p>
            </div>

            {trainer.experience && (
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Pervoja")}</h2>
                <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
                <p className="text-[#4A5568] leading-relaxed">{trainer.experience}</p>
              </div>
            )}

            {trainingsList.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Trajnimet qe mban")}</h2>
                <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
                <ul className="space-y-2">
                  {trainingsList.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#4A5568]"><i className="fas fa-graduation-cap mt-1" style={{ color: '#1d88a8' }}></i>{t}</li>
                  ))}
                </ul>
              </div>
            )}

            {trainerEvents.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Eventet")}</h2>
                <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
                <div className="space-y-3">
                  {trainerEvents.map(e => (
                    <Link key={e.id} to={`/evente/${e.slug || e.id}`} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div>
                        <p className="font-semibold text-sm text-[#0A0D10]">{e.title}</p>
                        <p className="text-xs text-[#828D9E] mt-1">{e.date} | {e.location}</p>
                      </div>
                      <i className="fas fa-arrow-right text-[#1CA3B0]"></i>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainerDetailPage;
